import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import WorkOutlineIcon from '@mui/icons-material/Work';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const STATUS = {
  disponivel: { label: 'Disponível', cor: '#16A34A', pulsa: true },
  em_campo:   { label: 'Em campo',   cor: '#2563EB', pulsa: true },
  em_reuniao: { label: 'Em reunião', cor: '#D97706', pulsa: true },
  ferias:     { label: 'Férias',     cor: '#7C3AED', pulsa: false },
  afastado:   { label: 'Afastado',   cor: '#94A3B8', pulsa: false },
};

const PERFIL = {
  diretor:    { label: 'Diretor',    bg: '#d4a574', fg: '#1a1a2e' },
  gestor:     { label: 'Gestor',     bg: '#1a1a2e', fg: '#ffffff' },
  consultor:  { label: 'Consultor',  bg: '#e94560', fg: '#ffffff' },
  financeiro: { label: 'Financeiro', bg: '#16A34A', fg: '#ffffff' },
  comercial:  { label: 'Comercial',  bg: '#2563EB', fg: '#ffffff' },
  marketing:  { label: 'Marketing',  bg: '#D97706', fg: '#ffffff' },
  rh:         { label: 'RH',         bg: '#7C3AED', fg: '#ffffff' },
};

const ESTADO_CIVIL_LABEL = {
  solteiro: 'Solteiro(a)',
  casado: 'Casado(a)',
  divorciado: 'Divorciado(a)',
  viuvo: 'Viúvo(a)',
  uniao_estavel: 'União estável',
};

const TIPO_CONTRATACAO_LABEL = {
  clt: 'CLT',
  pj: 'PJ',
  estagio: 'Estágio',
  outro: 'Outro',
};

function tempoDeCasa(dataAdmissao) {
  if (!dataAdmissao) return null;
  const inicio = new Date(dataAdmissao);
  const hoje = new Date();
  let meses = (hoje.getFullYear() - inicio.getFullYear()) * 12 + (hoje.getMonth() - inicio.getMonth());
  if (hoje.getDate() < inicio.getDate()) meses -= 1;
  if (meses < 0) return null;
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  if (anos === 0) return `${resto}m de casa`;
  if (resto === 0) return `${anos}a de casa`;
  return `${anos}a ${resto}m de casa`;
}

function Tile({ icon, label, cor, children }) {
  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid #eef0f3',
        borderLeft: `3px solid ${cor}`,
        borderRadius: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        backgroundColor: '#fbfbfd',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 26, height: 26, borderRadius: 1.5,
            backgroundColor: `${cor}1a`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: cor,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: '#94a3b8' }}
        >
          {label}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

function CampoInfo({ icon, label, valor, cor }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box
        sx={{
          width: 34, height: 34, borderRadius: 2, flexShrink: 0, mt: 0.2,
          backgroundColor: `${cor}16`, color: cor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: '#94a3b8', display: 'block' }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 14.5, color: '#1a1a2e', fontWeight: 500, mt: 0.2, wordBreak: 'break-word' }}>
          {valor || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

function SecaoTitulo({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
        color: '#e94560', display: 'block', mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

export default function Perfil() {
  const [membro, setMembro] = useState(null);
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/membros-equipe/me/').then((resMembro) => {
      setMembro(resMembro.data);
      return api.get(`/fichas-cadastrais/?membro_equipe=${resMembro.data.id}`);
    }).then((resFicha) => {
      setFicha(resFicha.data[0] || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <Carregando />;
  }
  if (!membro) {
    return <Typography color="text.secondary" textAlign="center" mt={4}>Perfil não encontrado.</Typography>;
  }

  const st = STATUS[membro.status] || STATUS.disponivel;
  const pf = PERFIL[membro.perfil] || { label: membro.perfil, bg: '#1a1a2e', fg: '#fff' };

  // Cargo e data de admissão vêm da Ficha Cadastral (RH) — o campo antigo do
  // MembroEquipe fica só como retorno de segurança, caso a ficha ainda não
  // tenha sido preenchida (decisão de 23/09/2026).
  const cargo = ficha?.cargo || membro.cargo;
  const dataAdmissao = ficha?.data_admissao || membro.data_admissao;
  const dataDemissao = ficha?.data_demissao;

  const rotuloCracha = cargo || pf.label;
  const casa = tempoDeCasa(dataAdmissao);
  const fichaPreenchida = ficha && !ficha.pendente_preenchimento;

  return (
    <Box>
      <style>{`
        @keyframes pulseGlow {
          0%   { box-shadow: 0 0 0 0 var(--pulse-color); }
          70%  { box-shadow: 0 0 0 14px rgba(0,0,0,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .anel-status { animation: none !important; }
        }
      `}</style>

      <PageHeader titulo="Meu Perfil" />

      <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <Box
          sx={{
            position: 'relative',
            background: 'linear-gradient(135deg, #13375F 0%, #0d2642 100%)',
            backgroundImage: `
              repeating-linear-gradient(45deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 26px),
              linear-gradient(135deg, #13375F 0%, #0d2642 100%)
            `,
            p: { xs: 2.5, sm: 3.5 },
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            flexWrap: 'wrap',
          }}
        >
          <Box
            className="anel-status"
            sx={{
              width: 84, height: 84, borderRadius: '50%',
              p: '3px', flexShrink: 0,
              border: `2px solid ${st.cor}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '--pulse-color': `${st.cor}55`,
              animation: st.pulsa ? 'pulseGlow 2.6s ease-out infinite' : 'none',
            }}
          >
            <Box
              sx={{
                width: '100%', height: '100%', borderRadius: '50%',
                backgroundColor: '#e94560',
                backgroundImage: membro.foto_url ? `url(${membro.foto_url})` : 'none',
                backgroundSize: 'cover', backgroundPosition: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 30, fontWeight: 700,
              }}
            >
              {!membro.foto_url && membro.nome_completo?.charAt(0)}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: 'white', lineHeight: 1.2 }}>
              {membro.nome_completo}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
              {membro.email}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.6,
                  backgroundColor: pf.bg, color: pf.fg,
                  px: 1.2, py: 0.4, borderRadius: 1.5,
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.03em',
                }}
              >
                {rotuloCracha.toUpperCase()}
              </Box>
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.6,
                  color: 'rgba(255,255,255,0.75)', fontSize: 12,
                }}
              >
                <RadioButtonCheckedIcon sx={{ fontSize: 12, color: st.cor }} />
                {st.label}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 260px))',
            gap: 2,
          }}
        >
          <Tile icon={<ApartmentIcon sx={{ fontSize: 15 }} />} label="Célula" cor="#2563EB">
            <Typography sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 600, color: '#1a1a2e' }}>
              {membro.celula?.nome || '—'}
            </Typography>
          </Tile>

          <Tile icon={<WorkOutlineIcon sx={{ fontSize: 15 }} />} label="Cargo" cor="#7C3AED">
            <Typography sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 600, color: '#1a1a2e' }}>
              {cargo || '—'}
            </Typography>
          </Tile>

          <Tile icon={<CalendarTodayIcon sx={{ fontSize: 15 }} />} label="Admissão" cor="#D97706">
            <Typography sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 600, color: '#1a1a2e' }}>
              {dataAdmissao ? dayjs(dataAdmissao).format('DD/MM/YYYY') : '—'}
            </Typography>
            {dataDemissao ? (
              <Typography sx={{ fontSize: 12, color: '#e94560', mt: 0.2, fontWeight: 600 }}>
                Desligado em {dayjs(dataDemissao).format('DD/MM/YYYY')}
              </Typography>
            ) : casa && (
              <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.2 }}>
                {casa}
              </Typography>
            )}
          </Tile>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid #e5e7eb',
          backgroundColor: 'white', p: { xs: 2.5, sm: 3.5 },
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} mb={0.3}>Meus dados</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Informações da sua ficha cadastral.
        </Typography>

        {!fichaPreenchida ? (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              backgroundColor: '#f8f9fa', borderRadius: 2, p: 2,
            }}
          >
            <InfoOutlinedIcon sx={{ color: '#94a3b8' }} />
            <Typography color="text.secondary">
              Sua ficha ainda não foi preenchida pelo RH.
            </Typography>
          </Box>
        ) : (
          <>
            <SecaoTitulo>Dados pessoais</SecaoTitulo>
            <Box
              sx={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 3, mb: 3,
              }}
            >
              <CampoInfo icon={<BadgeOutlinedIcon sx={{ fontSize: 18 }} />} label="CPF" valor={ficha.cpf} cor="#7C3AED" />
              <CampoInfo icon={<AssignmentIndOutlinedIcon sx={{ fontSize: 18 }} />} label="RG" valor={ficha.rg} cor="#7C3AED" />
              <CampoInfo
                icon={<CakeOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Data de nascimento"
                valor={ficha.data_nascimento ? dayjs(ficha.data_nascimento).format('DD/MM/YYYY') : ''}
                cor="#7C3AED"
              />
              <CampoInfo
                icon={<FavoriteBorderOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Estado civil"
                valor={ESTADO_CIVIL_LABEL[ficha.estado_civil]}
                cor="#7C3AED"
              />
              <CampoInfo icon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />} label="Telefone" valor={ficha.telefone} cor="#7C3AED" />
              <CampoInfo icon={<PlaceOutlinedIcon sx={{ fontSize: 18 }} />} label="Endereço" valor={ficha.endereco} cor="#7C3AED" />
              <CampoInfo
                icon={<ContactPhoneOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Contato de emergência"
                valor={ficha.contato_emergencia_nome && `${ficha.contato_emergencia_nome} — ${ficha.contato_emergencia_telefone || ''}`}
                cor="#7C3AED"
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            <SecaoTitulo>Dados contratuais</SecaoTitulo>
            <Box
              sx={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 3,
              }}
            >
              <CampoInfo
                icon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Tipo de contratação"
                valor={TIPO_CONTRATACAO_LABEL[ficha.tipo_contratacao]}
                cor="#D97706"
              />
              <CampoInfo
                icon={<AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Jornada de trabalho"
                valor={ficha.jornada_trabalho}
                cor="#D97706"
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}